import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import {
  FormControl,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { addDays, addYears, startOfDay, endOfDay } from 'date-fns';

interface PreferencesStepProps {
  onSubmit: (data: any) => void;
  disabled: boolean;
  isSubmitting: boolean;
  // Bulk targeting
  bulkEnabled?: boolean;
  siteId?: number;
  organizationId?: number;
}

import { useOrgSites } from '@features/sites/queries/use-org-sites.queries';

const ALL_BREAK_ROOMS_ID = '__ALL_BREAK_ROOMS__';

export function PreferencesStep({
  onSubmit,
  disabled,
  isSubmitting,
  bulkEnabled = false,
  siteId,
  organizationId,
}: PreferencesStepProps) {
  const { control, watch, setValue, handleSubmit } = useFormContext();

  // Watch form values
  const publishNow = watch('preferences.publishNow', true);
  const publishForever = watch('preferences.publishForever', false);
  const targetSiteIds = watch('preferences.targetSiteIds', [] as number[]);
  const applyBulkEditToAll = watch('preferences.applyBulkEditToAll', true);

  // Default dates
  const defaultStopShowingDateTime = endOfDay(addDays(new Date(), 14));
  const permanentStopShowingDateTime = endOfDay(addYears(new Date(), 50));
  const defaultStartShowingDateTime = startOfDay(new Date());

  // Fetch org sites with React Query
  const { data: orgSites = [], isLoading: loadingSites } = useOrgSites(
    organizationId,
    { enabled: bulkEnabled && !!organizationId },
  );

  // Diagnostic logging to verify bulk UI visibility and data
  // eslint-disable-next-line no-console
  console.log('[PreferencesStep]', {
    bulkEnabled,
    orgSitesCount: (orgSites || []).length,
    selectedCount: (targetSiteIds || []).length,
    siteId,
    organizationId,
  });

  const handlePublishNowToggle = (checked: boolean) => {
    setValue('preferences.publishNow', checked);
    if (checked) {
      const currentTime = new Date();
      setValue('preferences.startShowing', currentTime);
      setValue('preferences.startShowingTime', currentTime);
    }
  };

  const handlePublishForeverToggle = (checked: boolean) => {
    setValue('preferences.publishForever', checked);
    if (checked) {
      const endDate = permanentStopShowingDateTime;
      setValue('preferences.stopShowing', endDate);
      setValue('preferences.stopShowingTime', endDate);
    } else {
      const endDate = defaultStopShowingDateTime;
      setValue('preferences.stopShowing', endDate);
      setValue('preferences.stopShowingTime', endDate);
    }
  };

  const handleFormSubmit = handleSubmit(async (data) => {
    try {
      onSubmit(data);
    } catch (error) {
      console.error('Error creating announcement:', error);
    }
  });

  // Assigned properties modal state
  const [assignModalOpen, setAssignModalOpen] = React.useState(false);
  const [modalSelectedIds, setModalSelectedIds] = React.useState<number[]>(
    targetSiteIds?.length ? [...targetSiteIds] : [siteId!].filter(Boolean),
  );

  React.useEffect(() => {
    setModalSelectedIds(targetSiteIds?.length ? [...targetSiteIds] : [siteId!].filter(Boolean));
  }, [targetSiteIds, siteId]);

  const breakRoomIds = React.useMemo(
    () =>
      (orgSites || [])
        .filter((s: any) => (s?.name || '').toLowerCase().includes('break room'))
        .map((s: any) => s.id),
    [orgSites],
  );

  const allBreakRoomsOption = React.useMemo(
    () => ({ id: ALL_BREAK_ROOMS_ID, name: 'All Break Rooms' }),
    [],
  );

  const modalSelectedOptions = React.useMemo(() => {
    const ids = Array.isArray(modalSelectedIds) ? modalSelectedIds : [];
    const selected = (orgSites || []).filter((s: any) => ids.includes(s.id));
    const hasAllBreakRooms =
      breakRoomIds.length > 0 && breakRoomIds.every((id: number) => ids.includes(id));
    return hasAllBreakRooms ? [allBreakRoomsOption, ...selected] : selected;
  }, [modalSelectedIds, orgSites, breakRoomIds, allBreakRoomsOption]);

  const autocompleteOptions = React.useMemo(
    () => [allBreakRoomsOption, ...(orgSites as any[])],
    [allBreakRoomsOption, orgSites],
  );

  const selectedSites = (orgSites || []).filter((s) =>
    (targetSiteIds || []).includes(s.id),
  );

  const openAssignModal = () => setAssignModalOpen(true);
  const closeAssignModal = () => setAssignModalOpen(false);
  const saveAssignModal = () => {
    const next = Array.from(new Set([...(modalSelectedIds || []), siteId!].filter(Boolean)));
    setValue('preferences.targetSiteIds', next);
    if ((next?.length || 0) > 1 && applyBulkEditToAll === undefined) {
      setValue('preferences.applyBulkEditToAll', true);
    }
    closeAssignModal();
  };

  // Ensure the current property is always included in assigned list
  React.useEffect(() => {
    if (!Array.isArray(targetSiteIds)) return;
    if (!targetSiteIds.includes(siteId!)) {
      setValue('preferences.targetSiteIds', [...targetSiteIds, siteId!]);
    }
  }, [siteId]);

  return (
    <form className="flex flex-col gap-4" onSubmit={handleFormSubmit}>
      <FormControl fullWidth>
        <Typography variant="h6" gutterBottom>
          Display Duration (seconds)
        </Typography>
        <Controller
          name="preferences.displayDuration"
          control={control}
          rules={{ required: true, min: 1 }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              type="number"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        <div className="mt-6">
          <Controller
            name="preferences.publishNow"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={field.value}
                    onChange={(e) => handlePublishNowToggle(e.target.checked)}
                  />
                }
                label="Make available immediately"
              />
            )}
          />
        </div>

        {!publishNow && (
          <div className="mt-4">
            <Typography variant="h6" gutterBottom>
              Publish Starting
            </Typography>
            <div className="flex gap-4">
              <Controller
                name="preferences.startShowing"
                control={control}
                rules={{ required: !publishNow }}
                render={({ field: { value, ...field } }) => (
                  <DatePicker
                    {...field}
                    value={value ? new Date(value) : null}
                    label="Start Date"
                    minDate={new Date()}
                    disabled={publishNow}
                  />
                )}
              />
              <Controller
                name="preferences.startShowingTime"
                control={control}
                rules={{ required: !publishNow }}
                render={({ field: { value, ...field } }) => (
                  <TimePicker
                    {...field}
                    value={
                      value ? new Date(value) : defaultStartShowingDateTime
                    }
                    label="Start Time"
                    disabled={publishNow}
                    slots={{
                      openPickerIcon: () => null,
                    }}
                  />
                )}
              />
            </div>
          </div>
        )}

        <div className="mt-6">
          <Typography variant="h6" gutterBottom>
            Publish Until
          </Typography>
          {!publishForever && (
            <div className="flex gap-4">
              <Controller
                name="preferences.stopShowing"
                control={control}
                rules={{ required: !publishForever }}
                render={({ field: { value, ...field } }) => (
                  <DatePicker
                    {...field}
                    value={value ? new Date(value) : null}
                    label="End Date"
                    minDate={new Date()}
                    disabled={publishForever}
                  />
                )}
              />
              <Controller
                name="preferences.stopShowingTime"
                control={control}
                rules={{ required: !publishForever }}
                render={({ field: { value, ...field } }) => (
                  <TimePicker
                    {...field}
                    value={value ? new Date(value) : defaultStopShowingDateTime}
                    label="End Time"
                    disabled={publishForever}
                    slots={{
                      openPickerIcon: () => null,
                    }}
                  />
                )}
              />
            </div>
          )}

          <div className="mt-2">
            <Controller
              name="preferences.publishForever"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) =>
                        handlePublishForeverToggle(e.target.checked)
                      }
                    />
                  }
                  label="Make permanent"
                />
              )}
            />
          </div>
        </div>
        {bulkEnabled && (
          <div className="mt-6">
            <Typography variant="h6" gutterBottom>
              Assigned Properties
            </Typography>
            <div className="flex flex-wrap gap-1 mb-2">
              {selectedSites.length > 0 ? (
                selectedSites.map((s) => (
                  <Chip key={s.id} label={s.name} size="small" />
                ))
              ) : (
                <Chip label="Current Property" size="small" />
              )}
            </div>
            <Button
              size="small"
              variant="text"
              color="primary"
              onClick={() => setAssignModalOpen(true)}
              disabled={loadingSites}
            >
              Assign to more properties
            </Button>
            {(targetSiteIds?.length || 0) > 1 && (
              <div className="mt-2">
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!applyBulkEditToAll}
                      onChange={(e) =>
                        setValue(
                          'preferences.applyBulkEditToAll',
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label={`Apply updates to all selected locations (${targetSiteIds.length})`}
                />
              </div>
            )}
          </div>
        )}

        <div className="flex justify-center mt-4">
          <Button
            variant="contained"
            type="submit"
            disabled={disabled || isSubmitting}
            data-qa="slide-preferences-submit-button"
          >
            {isSubmitting ? 'Saving...' : 'Finish'}
          </Button>
        </div>
      </FormControl>
      {/* Assign Properties Modal */}
      <Dialog open={assignModalOpen} onClose={() => setAssignModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Assign to properties</DialogTitle>
        <DialogContent dividers>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={autocompleteOptions}
            getOptionLabel={(option: any) => option?.name || ''}
            value={modalSelectedOptions}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            onChange={(_, value: any[], reason, details) => {
              if (reason === 'selectOption' && details?.option?.id === ALL_BREAK_ROOMS_ID) {
                const nextIds = Array.from(
                  new Set([...(modalSelectedIds || []), ...breakRoomIds]),
                );
                setModalSelectedIds(nextIds);
                return;
              }

              if (reason === 'removeOption' && details?.option?.id === ALL_BREAK_ROOMS_ID) {
                const nextIds = (modalSelectedIds || []).filter(
                  (id) => !breakRoomIds.includes(id),
                );
                setModalSelectedIds(nextIds);
                return;
              }

              if (reason === 'clear') {
                setModalSelectedIds([siteId!].filter(Boolean));
                return;
              }

              const nextIds = Array.from(
                new Set(
                  value
                    .filter((v) => v?.id !== ALL_BREAK_ROOMS_ID)
                    .map((v) => v.id)
                    .filter(Boolean),
                ),
              );
              setModalSelectedIds(nextIds);
            }}
            renderOption={(props, option: any, { selected }) => (
              <li {...props} key={String(option.id)}>
                <Checkbox style={{ marginRight: 8 }} checked={selected} />
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Search properties" placeholder="Type to search" />
            )}
            renderTags={(tagValue, getTagProps) => {
              const allBreakRoomsIndex = tagValue.findIndex(
                (option: any) => option?.id === ALL_BREAK_ROOMS_ID,
              );
              if (allBreakRoomsIndex >= 0) {
                const tagProps = getTagProps({ index: allBreakRoomsIndex });
                return [
                  <Chip
                    {...tagProps}
                    key={ALL_BREAK_ROOMS_ID}
                    label="All Break Rooms"
                    size="small"
                  />,
                ];
              }
              return tagValue.map((option: any, index: number) => (
                <Chip
                  {...getTagProps({ index })}
                  key={String(option.id)}
                  label={option.name}
                  size="small"
                />
              ));
            }}
            loading={loadingSites}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignModalOpen(false)}>Cancel</Button>
          <Button onClick={saveAssignModal} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </form>
  );
}
